import { type BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'
import { type LoginResponseData, type LoginRequestData, type OrganizationCreationData, type OrganizationData, UserData, UserRegisterData } from '../../types'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { createAxiosInstance } from '../common'

export const apiAuthBaseQuery =
  (): BaseQueryFn<
  {
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
    headers?: AxiosRequestConfig['headers']
  },
  unknown,
  unknown
  > =>
    async ({ url, method, data, params, headers }) => {
      const api = createAxiosInstance(`${String(process.env.REACT_APP_ENGINE_API_URL)}`)
      try {
        const result = await api(url, {
          method,
          data,
          params,
          headers
        })

        return { data: result.data }
      } catch (axiosError) {
        console.dir(axiosError)

        const err = axiosError as AxiosError

        throw err
      }
    }

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: apiAuthBaseQuery(),
  endpoints: (builder) => ({
    loginUser: builder.mutation<LoginResponseData, { data: LoginRequestData }>({
      query ({ data }) {
        return {
          url: '/auth/login',
          method: 'post',
          data,
          prepareHeaders: (headers: any) => {
            headers.set('Content-Type', 'application/json')
            return headers
          },
          responseHandler: async (response: Response) => await response.json()
        }
      }
    }),
    registerOrganization: builder.mutation<OrganizationData, { data: OrganizationCreationData }>({
      query ({ data }) {
        return {
          url: '/organization',
          method: 'post',
          data,
          prepareHeaders: (headers: any) => {
            headers.set('Content-Type', 'application/json')
            return headers
          },
          responseHandler: async (response: Response) => await response.json()
        }
      }
    }),
    registerUser: builder.mutation<UserData, { data: UserRegisterData }>({
      query ({ data }) {
        return {
          url: '/auth/register',
          method: 'post',
          data,
          prepareHeaders: (headers: any) => {
            headers.set('Content-Type', 'application/json')
            return headers
          },
          responseHandler: async (response: Response) => await response.json()
        }
      }
    })
  })
})

export const { useLoginUserMutation, useRegisterOrganizationMutation, useRegisterUserMutation } = authApi
